import {
  Alert,
  Button,
  DatePicker,
  Input,
  Select,
  Space,
  TableProps,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import CaretRight from "../../../assets/icons/CaretRight";
import CloseIcon from "../../../assets/icons/CloseIcon";
import DateIcon from "../../../assets/icons/DateIcon";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import GoAlertFill from "../../../assets/icons/GoAlertFill";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SortIcon from "../../../assets/icons/SortIcon";
import PayReportRequestButtonModal from "../../../components/Payroll/PayReportRequestButtonModal";
import SalaryDisbursement from "../../../components/Payroll/SalaryDisbursement";
import CustomTable from "../../../components/ui/CustomTable";
import colors from "../../../constants/colors";
import { useGetDepartmentsQuery } from "../../../features/department/departmentApi";
import { useGetDesignationsQuery } from "../../../features/designation/designationApi";
import {
  useGetSalaryReportQuery,
  usePaySlipDisbursementMutation,
} from "../../../features/payroll/payrollApi";
import ReGenerateSalaryModal from "../../../features/payroll/ReGenerateSalaryModal";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import {
  GetSalaryReportParamsType,
  GetSalaryReportResponseType,
} from "../../../types/payroll";
import { countParPageSerialNumber } from "../../../utils/number";
import {
  addKeyInArray,
  formatSouthAsianWithDecimals,
  selectArrayOption,
  selectSearchOption,
  underscoreToCapitalizeString,
} from "../../../utils/utils";
import BkashDisbursementModal from "./BkashDisbursementModal";
import DownloadSuccessModal from "./DownloadSuccessModal";
import { payrollBetaSettingsTabList } from "./PayrollSummary";

const searchTypeSelect = [
  {
    label: "Name",
    value: "name",
  },
  {
    label: "Employee ID",
    value: "employee_id",
  },
];

type ModalType =
  | "BKASH_DISBURSEMENT"
  | "PAY_REPORT"
  | "REPORT_REQUEST"
  | "REGENERATE"
  | "SALARY_DISBURSEMENT";

export default function PayReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  // const [selectedData, setSelectedData] = useState<string[]>([]);
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    handleFilter,
    filter,
    keyword,
    handleSearch,
  } = useTable<GetSalaryReportParamsType>({
    defaultFilter: {
      search_type: "name",
      // month_year: dayjs().format("MM-YYYY"),
    },
  });
  const { open, handleClose, handleOpen } = useModal<undefined, ModalType>();
  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    data: designationData,
    isError: isDesignationError,
    error: designationError,
  } = useGetDesignationsQuery(
    {
      params: {
        limit: 0,
        skip: 0,
        ...(filter?.department_id
          ? {
              business_department_id: filter?.department_id?.toString() || "",
            }
          : {}),
        is_published: 1,
      },
    },
    {
      skip: !filter?.department_id,
    }
  );
  const { isLoading, isError, error, data } = useGetSalaryReportQuery({
    params: {
      ...filter,
      page,
      limit,
      search_type: undefined,
      skip: undefined,
      business_payslip_id: id,
      ...(!id ? { month_year: filter.month_year } : { month_year: undefined }),
      ...(keyword ? { search_type: filter.search_type } : {}),
      search: keyword,
    },
  });

  const [
    paySlipDisbursement,
    {
      isError: isDisbursementError,
      error: disbursementError,
      isLoading: isDisbursementLoading,
      isSuccess: isDisbursementSuccess,
    },
  ] = usePaySlipDisbursementMutation();
  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isDesignationError, designationError, "Get designation fail");
  useError(isError, error, "Get payroll report data fail");
  useError(isDisbursementError, disbursementError, "Disbursement data fail");
  // useError(isReGenerateError, reGenerateError, "Disbursement data fail");

  const isShowModal = data?.data?.salaryData?.filter(
    (item) => item?.status === "pending"
  );

  const columns: TableProps<
    GetSalaryReportResponseType["data"]["salaryData"][0]
  >["columns"] = [
    {
      title: "#SL",
      width: 60,
      align: "center",
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "ID",
      dataIndex: "employeeId",
      sorter: (a, b) => Number(a?.employeeId ?? 0) - Number(b?.employeeId ?? 0),
      sortIcon: () => <SortIcon />,
      render: (val, record) => (
        <span className="flex items-center gap-1.5">
          {record?.status === "pending" ? (
            <Tooltip placement="top" title="Salary not disbursed!">
              <GoAlertFill
                style={{
                  color: colors?.danger?.color,
                  fontSize: 18,
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          ) : null}{" "}
          {val ?? "n/a"}
        </span>
      ),
    },
    {
      title: "Employee Name",
      dataIndex: "employeeName",
      render: (val) => val || "n/a",
      sorter: (a, b) => a?.employeeName?.localeCompare(b?.employeeName),
      sortIcon: () => <SortIcon />,
    },
    {
      title: "Department",
      dataIndex: "departmentName",
      render: (val) => val || "n/a",
    },
    {
      title: "Gross",
      // dataIndex: "salaryBreakdown",
      render: (val) =>
        formatSouthAsianWithDecimals(val?.salaryBreakdown?.gross || 0),
      sortIcon: () => <SortIcon />,
      sorter: (a, b) =>
        Number(a?.salaryBreakdown?.gross ?? 0) -
        Number(b?.salaryBreakdown?.gross ?? 0),
    },
    {
      title: "Addition",
      // dataIndex: "salaryBreakdown",
      render: (val) =>
        formatSouthAsianWithDecimals(val?.salaryBreakdown?.addition || 0),
      sortIcon: () => <SortIcon />,
      sorter: (a, b) =>
        Number(a?.salaryBreakdown?.addition ?? 0) -
        Number(b?.salaryBreakdown?.addition ?? 0),
    },
    {
      title: "Deduction",
      // dataIndex: "salaryBreakdown",
      render: (val) =>
        formatSouthAsianWithDecimals(val?.salaryBreakdown?.deduction || 0),
      sortIcon: () => <SortIcon />,
      sorter: (a, b) =>
        Number(a?.salaryBreakdown?.addition ?? 0) -
        Number(b?.salaryBreakdown?.addition ?? 0),
    },
    {
      title: "Net Payable",
      // dataIndex: "salaryBreakdown",
      render: (val) =>
        formatSouthAsianWithDecimals(
          val?.salaryBreakdown?.gross +
            val?.salaryBreakdown?.addition -
            val?.salaryBreakdown?.deduction || 0
        ),
      sortIcon: () => <SortIcon />,
      sorter: (a, b) => {
        const totalSalaryBreakdownA =
          a?.salaryBreakdown?.gross +
            a?.salaryBreakdown?.addition -
            a?.salaryBreakdown?.deduction || 0;
        const totalSalaryBreakdownB =
          b?.salaryBreakdown?.gross +
            b?.salaryBreakdown?.addition -
            b?.salaryBreakdown?.deduction || 0;

        return (
          Number(totalSalaryBreakdownA ?? 0) -
          Number(totalSalaryBreakdownB ?? 0)
        );
      },
    },
    {
      title: "Action",
      dataIndex: "id",
      render: (val) => (
        <Link className="px-3" to={`/admin/payroll/pay-report/${id}/${val}`}>
          <Button className="w-7 h-7 flex items-center justify-center">
            <CaretRight />
          </Button>
        </Link>
      ),
    },
  ];

  const totalSum = data?.data?.totalSum;
  const netPayable =
    (totalSum?.gross || 0) +
    (totalSum?.addition || 0) -
    (totalSum?.deduction || 0);

  // success toast message
  useEffect(() => {
    if (isDisbursementSuccess) {
      toast("success", "Disbursement successfully disbursed!");
      return handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisbursementSuccess]);

  useEffect(() => {
    if (id && data?.data?.salaryData?.length) {
      handleFilter(
        "month_year",
        dayjs(data?.data?.salaryData?.[0]?.schedule_date)?.format("MM-YYYY")
      );
    } else if (!id && !filter?.month_year) {
      handleFilter("month_year", dayjs().format("MM-YYYY"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data?.salaryData, filter?.month_year, id]);

  // success toast message
  // useEffect(() => {
  //   if (isReGenerateSuccess) {
  //     toast("success", "Salary successfully re-generated");
  //     // setSelectedData([]);
  //   }
  // }, [isReGenerateSuccess]);

  return (
    <SidebarLayout title="Payroll" tabList={payrollBetaSettingsTabList}>
      {open.type === "BKASH_DISBURSEMENT" && (
        <BkashDisbursementModal
          open={open.open}
          handleClose={handleClose}
          isDisbursementLoading={isDisbursementLoading}
          handleDisbursement={() =>
            paySlipDisbursement({
              payslipIds: data?.data?.salaryData?.map((item) => item?.id),
            })
          }
        />
      )}
      {open.type === "PAY_REPORT" && (
        <DownloadSuccessModal open={open.open} handleClose={handleClose} />
      )}
      {open.type === "REPORT_REQUEST" && (
        <PayReportRequestButtonModal
          open={open.open}
          handleClose={handleClose}
        />
      )}
      {open.type === "REGENERATE" && (
        <ReGenerateSalaryModal open={open.open} handleClose={handleClose} />
      )}
      {open.type === "SALARY_DISBURSEMENT" && (
        <SalaryDisbursement open={open.open} handleClose={handleClose} />
      )}
      <div className="bg-white rounded-md border border-extra-light p-4 mb-4">
        <div className="flex flex-wrap justify-between gap-2 border-b border-b-extra-light pb-4">
          <Space size={8}>
            <Button
              onClick={() => handleOpen({ type: "BKASH_DISBURSEMENT" })}
              className="!text-[#4A72FF] !border-[#4A72FF]"
              // loading={isDisbursementLoading}
            >
              bKash Disbursement
            </Button>
            {/* <Button
              loading={excelLoading}
              onClick={handleDownload}
              className="!text-[#4A72FF] !border-[#4A72FF] flex gap-2 items-center"
            >
              <DownloadIcon />
              <span>Download Pay Report</span>
            </Button>
            <Button
              loading={excelDetailsLoading}
              onClick={handleDownloadDetails}
              className="!text-[#4A72FF] !border-[#4A72FF] flex gap-2 items-center"
            >
              <DownloadIcon />
              <span>Download Pay Report Details</span>
            </Button> */}
          </Space>

          <Space size={8}>
            <Button
              onClick={() => handleOpen({ type: "REPORT_REQUEST" })}
              className="download-button"
              icon={<DownloadIcon />}
            >
              Report Requests
            </Button>
            <Button
              className="modal-button outline-button-primary"
              // type="primary"
              size="middle"
              style={{ minWidth: "150px" }}
              onClick={() => handleOpen({ type: "SALARY_DISBURSEMENT" })}
            >
              Salary Disbursement
            </Button>
            <Button
              className="modal-button"
              type="primary"
              htmlType="submit"
              size="middle"
              style={{ minWidth: "150px" }}
              // onClick={() => {
              //   if (id && selectedData?.length)
              //     reGenerateSalary({
              //       business_payslip_id: Number(id),
              //       business_member_ids: selectedData,
              //     });
              // }}
              // disabled={!selectedData?.length}
              // loading={isReGenerateLoading}
              onClick={() => handleOpen({ type: "REGENERATE" })}
            >
              Re-generate Salary
            </Button>
          </Space>
        </div>
        <div className="flex gap-5 items-center pt-4">
          <h5 className="text-header text-base font-medium">Total:</h5>

          <div className="flex justify-between text-[#c0421c] bg-[#ffd3c0] text-base font-medium p-2 flex-1 rounded-md">
            <div>
              Gross: BDT {formatSouthAsianWithDecimals(totalSum?.gross || 0)}
            </div>
            <div>
              Addition: BDT{" "}
              {formatSouthAsianWithDecimals(totalSum?.addition || 0)}
            </div>
            <div>
              Deduction: BDT{" "}
              {formatSouthAsianWithDecimals(totalSum?.deduction || 0)}
            </div>
            <div>
              Net Payable: BDT {formatSouthAsianWithDecimals(netPayable)}
            </div>
          </div>
        </div>
      </div>

      {isShowModal?.length ? (
        <Alert
          message="One or more employees have not their salary disbursed."
          type="warning"
          showIcon
          closable
          icon={<GoAlertFill style={{ color: "#fff", fontSize: 20 }} />}
          closeIcon={<CloseIcon width={10} height={10} />}
          className="mb-4 rounded bg-[#f59e0b] font-medium text-sm text-white !px-4 !py-2.5"
        />
      ) : null}

      <CustomTable
        loading={isLoading}
        page={page}
        limit={limit}
        columns={columns}
        dataSource={addKeyInArray(data?.data?.salaryData)}
        total={data?.data?.total}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            {
              <DatePicker
                format="MMM YYYY"
                style={{ width: 140 }}
                suffixIcon={<DateIcon />}
                className="clear-button-hide"
                value={
                  filter?.month_year
                    ? dayjs(filter?.month_year, "MM-YYYY")
                    : undefined
                }
                onChange={(date) => {
                  handleFilter("month_year", date?.format("MM-YYYY"));
                  if (id) {
                    navigate("/admin/payroll/pay-report");
                  }
                }}
                picker="month"
                disabled={isLoading}
              />
            }
            <Select
              showSearch
              placeholder="Search Type"
              value={filter.search_type}
              options={searchTypeSelect}
              onChange={(val) => handleFilter("search_type", val)}
              filterOption={selectSearchOption}
              className="min-w-[110px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
            />
            <Input
              suffix={<SearchIcon />}
              placeholder={`Search by ${underscoreToCapitalizeString(
                filter.search_type || "",
                true
              )}`}
              style={{ minWidth: 180, maxWidth: 200 }}
              onChange={(val) => handleSearch(val)}
            />
            <Select
              allowClear
              showSearch
              placeholder="Department"
              filterOption={selectSearchOption}
              options={selectArrayOption(
                departmentData?.data?.departments || [],
                "name",
                "id"
              )}
              className="min-w-[130px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              onChange={(val) => {
                handleFilter("department_id", val);
                handleFilter("designation_id", undefined);
              }}
            />
            <Select
              allowClear
              showSearch
              placeholder="Designation"
              filterOption={selectSearchOption}
              options={selectArrayOption(
                designationData?.data?.roles || [],
                "name",
                "id"
              )}
              className="min-w-[130px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              onChange={(val) => handleFilter("designation_id", val)}
              disabled={!filter.department_id}
            />
          </>
        }
        // rowSelection={{
        //   type: "checkbox",
        //   selectedRowKeys: selectedData,
        //   onChange: (key) => {
        //     setSelectedData(key as string[]);
        //   },
        //   columnWidth: 40,
        // }}
      />
    </SidebarLayout>
  );
}
